import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, DollarSign, Clock, Share2, Pencil, ShieldCheck, Paperclip, CheckCircle2, Bookmark, BookmarkCheck } from "lucide-react";
import { categoryFor } from "../lib/categories";
import { fetchApplyUrl, submitLead, saveLead, getSavedLead, clearSavedLead, pushLocalLead, saveJob, unsaveJob } from "../lib/api";
import { uploadResume, trackJobView } from "../lib/employerApi";
import { CategoryIcon } from "../lib/categoryIcons";
import { useAuth } from "../lib/auth";
import ShareMenu from "./ShareMenu";
import GoogleButton from "./GoogleButton";

const CA_STATES = new Set(["ON","BC","QC","AB","MB","SK","NS","NB","NL","PE","YT","NT","NU"]);

function indeedFallback(job) {
    const cc = CA_STATES.has(job.state || "") ? "ca" : "us";
    const base = cc === "ca" ? "https://ca.indeed.com" : "https://www.indeed.com";
    const q = encodeURIComponent(job.title || "");
    const l = encodeURIComponent(job.city || "");
    return `${base}/jobs?q=${q}&l=${l}`;
}

export default function ApplyModal({ job, onClose, onToast }) {
    const { user, login } = useAuth();
    const savedLocal = getSavedLead();
    const initialSaved = user
        ? {
              name: user.name || savedLocal?.name || "",
              email: user.email,
              phone: user.phone || savedLocal?.phone || "",
          }
        : savedLocal;
    const [saved, setSaved] = useState(initialSaved);
    const needsPhone = user && !(initialSaved && initialSaved.phone);
    const [editing, setEditing] = useState(!initialSaved || needsPhone);
    const [name, setName] = useState(initialSaved?.name || "");
    const [phone, setPhone] = useState(initialSaved?.phone || "");
    const [email, setEmail] = useState(initialSaved?.email || "");

    // Fire a best-effort view impression for employer-posted jobs so the
    // employer analytics dashboard can show engagement. Server dedupes per
    // (viewer, job) inside a 10-min window so refreshes don't inflate counts.
    useEffect(() => {
        if (job && job.src === "employer" && job.id) {
            trackJobView(job.id);
        }
    }, [job?.id, job?.src]);

    // Rehydrate state when auth resolves after modal mounts (e.g. deep-link #job=<id>).
    useEffect(() => {
        if (!user) return;
        const merged = {
            name: user.name || savedLocal?.name || name || "",
            email: user.email,
            phone: user.phone || savedLocal?.phone || phone || "",
        };
        setSaved(merged);
        setName(merged.name);
        setEmail(merged.email);
        setPhone(merged.phone);
        setEditing(!merged.phone); // only require edit if no phone yet
    }, [user?.user_id]);
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [showShare, setShowShare] = useState(false);
    // Resume upload
    const [resume, setResume] = useState(null); // { path, filename, size }
    const [resumeUploading, setResumeUploading] = useState(false);
    const [resumeError, setResumeError] = useState("");
    const fileInputRef = useRef(null);
    // Save/bookmark state
    const [isSaved, setIsSaved] = useState(false);
    const [savingBookmark, setSavingBookmark] = useState(false);

    // Check saved state when modal opens.
    useEffect(() => {
        if (!user || !job?.id) {
            setIsSaved(false);
            return;
        }
        (async () => {
            try {
                const { fetchSavedIds } = await import("../lib/api");
                const ids = await fetchSavedIds();
                setIsSaved(ids.includes(job.id));
            } catch { /* ignore */ }
        })();
    }, [user, job?.id]);

    const toggleBookmark = async () => {
        if (!user) {
            onToast && onToast("Sign in to save jobs for later.");
            login();
            return;
        }
        setSavingBookmark(true);
        try {
            if (isSaved) {
                await unsaveJob(job.id);
                setIsSaved(false);
                onToast && onToast("Removed from saved jobs.");
            } else {
                await saveJob(job.id);
                setIsSaved(true);
                onToast && onToast("Saved! Find it under ☰ → Saved jobs.");
            }
        } catch {
            onToast && onToast("Couldn't update saved jobs — please try again.");
        } finally {
            setSavingBookmark(false);
        }
    };

    const onPickResume = async (e) => {
        setResumeError("");
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setResumeError("File too large (max 5 MB).");
            return;
        }
        setResumeUploading(true);
        try {
            const r = await uploadResume(file);
            setResume({ path: r.path, filename: r.filename, size: r.size });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Upload failed — try a PDF or DOCX under 5 MB.";
            setResumeError(msg);
        } finally {
            setResumeUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    if (!job) return null;
    const cat = categoryFor(job.cat);

    // If user is signed in, consent was captured at login — skip the tick requirement.
    const consentSatisfied = !!user || consent;

    const validate = () => {
        if (!name.trim() || name.trim().length < 2) return "Enter your full name.";
        const digits = (phone.match(/\d/g) || []).length;
        if (digits < 10) return "Enter a valid mobile (min 10 digits).";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Enter a valid email address.";
        if (!consentSatisfied) return "Please tick the consent box to continue.";
        return "";
    };

    const runApply = async (leadPayload) => {
        setSubmitting(true);
        // Open the popup SYNCHRONOUSLY on the user gesture so browsers don't block it.
        // Do NOT use noopener/noreferrer — that returns null and we lose the handle.
        const w = window.open("about:blank", "_blank");
        // Render a friendly loading state via safe DOM APIs (createElement / textContent
        // / appendChild). We do NOT use document.write, innerHTML, or any string-based HTML.
        try {
            if (w && w.document && w.document.body) {
                w.document.title = "Redirecting to employer…";
                const style = w.document.createElement("style");
                style.textContent =
                    "body{margin:0;font-family:system-ui,-apple-system,sans-serif;background:#fff2ea;color:#1a1e2c;height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;text-align:center;padding:24px}" +
                    ".ring{width:44px;height:44px;border-radius:50%;border:4px solid #ffdccb;border-top-color:#e85812;animation:s 1s linear infinite}" +
                    "@keyframes s{to{transform:rotate(360deg)}}" +
                    "h1{font-size:18px;margin:0;font-weight:700}p{font-size:13px;margin:0;color:#4a536f}";
                w.document.head.appendChild(style);
                const ring = w.document.createElement("div");
                ring.className = "ring";
                const h1 = w.document.createElement("h1");
                h1.textContent = "Taking you to the employer…";
                const p = w.document.createElement("p");
                p.textContent = "Please do not close this tab.";
                w.document.body.appendChild(ring);
                w.document.body.appendChild(h1);
                w.document.body.appendChild(p);
            }
        } catch {
            /* cross-origin about:blank quirk — ignore */
        }
        const consentAt = new Date().toISOString();
        const localEntry = {
            ...leadPayload,
            consent_at: consentAt,
            job_title: job.title,
            company: job.company,
            city: job.city,
            job_url: job.url,
            job_id: job.id,
            created_at: consentAt,
        };
        saveLead({ name: leadPayload.name, phone: leadPayload.phone, email: leadPayload.email });
        pushLocalLead(localEntry);
        try {
            await submitLead({
                ...leadPayload,
                consent: true,
                consent_at: consentAt,
                job_title: job.title,
                company: job.company,
                city: job.city,
                job_url: job.url,
                job_id: job.id,
                resume_path: resume?.path || "",
                resume_filename: resume?.filename || "",
            });
        } catch {
            /* ignore */
        }
        let url = "";
        try {
            url = await fetchApplyUrl(job.id);
        } catch {
            /* fall back below */
        }
        if (!url) url = job.url || "";
        if (/google\./.test(url)) url = "";
        const finalUrl = url || indeedFallback(job);
        if (w) {
            try {
                w.location.href = finalUrl;
            } catch {
                w.location.replace(finalUrl);
            }
        } else {
            // Popup was blocked — navigate the current tab as a last resort.
            window.location.href = finalUrl;
        }
        setSubmitting(false);
        onToast("Application sent 🎉 Good luck!");
        setTimeout(() => onClose(), 400);
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        const v = validate();
        if (v) {
            setError(v);
            return;
        }
        setError("");
        const lead = { name: name.trim(), phone: phone.trim(), email: email.trim() };
        setSaved(lead);
        setEditing(false);
        await runApply(lead);
    };

    const handleOneTap = async () => {
        if (!saved) return;
        await runApply(saved);
    };

    const shareUrl = `${window.location.origin}${window.location.pathname}#job=${encodeURIComponent(job.id)}`;
    const shareText = `${job.title} @ ${job.company} — ${job.loc || job.city}`;
    const isCanada = CA_STATES.has(job.state || "");
    const isEmployerPosted = job.src === "employer";

    // Reusable resume field (rendered inline via renderResumeField, defined outside JSX
    // to avoid the "unstable nested component" warning while keeping access to state).
    const renderResumeField = () => (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-3 flex items-center gap-3">
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={onPickResume}
                className="hidden"
                data-testid="apply-resume-input"
            />
            {resume ? (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-5 h-5" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[13px] font-display font-semibold text-[#1a1e2c] truncate">{resume.filename}</div>
                        <div className="text-[11px] text-slate-500">Resume attached ({Math.round((resume.size || 0) / 1024)} KB)</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setResume(null)}
                        className="ml-auto text-[11.5px] text-slate-500 hover:text-red-500 underline"
                        data-testid="apply-resume-remove"
                    >
                        Remove
                    </button>
                </div>
            ) : (
                <>
                    <span className="w-9 h-9 rounded-xl bg-[#fff2ea] text-[#e85812] flex items-center justify-center shrink-0">
                        <Paperclip className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-display font-semibold text-[#1a1e2c]">
                            Attach resume (optional)
                        </div>
                        <div className="text-[11px] text-slate-500">PDF / DOC / DOCX up to 5 MB — stands out to employers.</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={resumeUploading}
                        className="btn-ghost !py-1.5 !px-3 !text-xs shrink-0"
                        data-testid="apply-resume-upload-btn"
                    >
                        {resumeUploading ? "Uploading…" : "Upload"}
                    </button>
                </>
            )}
        </div>
    );

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[500] bg-[#1a1e2c]/60 backdrop-blur-sm flex md:items-center items-end justify-center p-0 md:p-4"
                onClick={onClose}
                data-testid="apply-modal"
            >
                <motion.div
                    initial={{ y: 32, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 32, opacity: 0 }}
                    transition={{ type: "spring", damping: 25, stiffness: 220 }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full md:max-w-lg bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
                >
                    {/* Header */}
                    <div
                        className="relative p-5 pb-4"
                        style={{ background: `linear-gradient(180deg, ${cat.color}, #ffffff)` }}
                    >
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center"
                            data-testid="apply-close-btn"
                            aria-label="close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <div className="flex items-start gap-3">
                            <div className="w-14 h-14 rounded-2xl bg-white shadow flex items-center justify-center shrink-0">
                                <CategoryIcon catId={cat.id} size={26} />
                            </div>
                            <div className="min-w-0 flex-1">
                                {isEmployerPosted && (
                                    <span
                                        className="inline-flex items-center gap-1 bg-[#e85812] text-white text-[10px] font-display font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-1"
                                        data-testid="apply-featured-badge"
                                    >
                                        ★ Featured employer
                                    </span>
                                )}
                                <div
                                    className="font-display font-extrabold text-[19px] leading-tight text-[#1a1e2c]"
                                    data-testid="apply-job-title"
                                >
                                    {job.title}
                                </div>
                                <div className="text-[14px] text-slate-700 mt-0.5">{job.company}</div>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[13px] text-slate-700">
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {job.loc || job.city}
                            </span>
                            {job.pay && (
                                <span className="inline-flex items-center gap-1 font-display font-bold text-[#e85812]">
                                    <DollarSign className="w-3.5 h-3.5" />
                                    {job.pay}
                                </span>
                            )}
                            {job.shift && (
                                <span className="inline-flex items-center gap-1">
                                    <Clock className="w-3.5 h-3.5" />
                                    {job.shift}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5 pt-3">
                        {!user && !saved && (
                            <div className="mb-3">
                                <GoogleButton onClick={login} label="Continue with Google — skip consent tick" />
                                <div className="text-center my-3 text-[11px] text-slate-400 uppercase tracking-wide font-display font-semibold">
                                    or apply without an account
                                </div>
                            </div>
                        )}
                        {saved && !editing ? (
                            <div className="animate-fade-up">
                                <div className="bg-[#fff2ea] border border-[#ffdccb] rounded-2xl p-4">
                                    <div className="text-[12px] font-display font-semibold text-[#e85812] uppercase tracking-wide flex items-center gap-1.5">
                                        <ShieldCheck className="w-3.5 h-3.5" />
                                        {user ? "signed in — apply in 1 tap" : "1-tap apply as"}
                                    </div>
                                    <div className="mt-2 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-display font-bold text-[15.5px] text-[#1a1e2c] truncate">
                                                {saved.name}
                                            </div>
                                            <div className="text-[12.5px] text-slate-600 truncate">
                                                {saved.phone ? `${saved.phone} · ` : ""}
                                                {saved.email}
                                            </div>
                                        </div>
                                        <button
                                            className="btn-ghost !px-3 !py-2 !text-xs"
                                            onClick={() => setEditing(true)}
                                            data-testid="apply-edit-btn"
                                        >
                                            <Pencil className="w-3.5 h-3.5" /> Edit
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={handleOneTap}
                                    disabled={submitting}
                                    className="btn-primary w-full mt-4 !py-3.5 !text-[15px]"
                                    data-testid="apply-one-tap-btn"
                                >
                                    {submitting ? "Applying…" : "Apply now →"}
                                </button>
                                <div className="mt-3">
                                    {renderResumeField()}
                                    {resumeError && (
                                        <div className="text-[12px] text-red-600 mt-1" data-testid="apply-resume-error">
                                            {resumeError}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="animate-fade-up">
                                <div className="space-y-3">
                                    <div className="m3-field">
                                        <input
                                            id="fld-name"
                                            type="text"
                                            placeholder=" "
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            data-testid="apply-name-input"
                                            autoComplete="name"
                                        />
                                        <label htmlFor="fld-name">Full name</label>
                                    </div>
                                    <div className="m3-field">
                                        <input
                                            id="fld-phone"
                                            type="tel"
                                            placeholder=" "
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            data-testid="apply-phone-input"
                                            autoComplete="tel"
                                        />
                                        <label htmlFor="fld-phone">Mobile number</label>
                                    </div>
                                    <div className="m3-field">
                                        <input
                                            id="fld-email"
                                            type="email"
                                            placeholder=" "
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            data-testid="apply-email-input"
                                            autoComplete="email"
                                        />
                                        <label htmlFor="fld-email">Email</label>
                                    </div>
                                    {user ? (
                                        <div className="flex items-start gap-2 text-[12.5px] text-slate-600 mt-1 bg-slate-50 rounded-xl p-3">
                                            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                                            <span>
                                                Signed in as <b>{user.email}</b>. You agreed to share your details at sign-in — no need to re-tick.
                                            </span>
                                        </div>
                                    ) : (
                                        <label className="flex items-start gap-2 mt-1 text-[12.5px] text-slate-700 cursor-pointer select-none leading-snug">
                                            <input
                                                type="checkbox"
                                                checked={consent}
                                                onChange={(e) => setConsent(e.target.checked)}
                                                className="mt-0.5 accent-[#e85812] w-4 h-4 shrink-0"
                                                data-testid="apply-consent-checkbox"
                                            />
                                            <span>
                                                I agree to share my name, mobile and email with the employer and to be contacted by <b>Dailyworkjobs</b> and this
                                                employer via phone, SMS or email about this and similar jobs — including automated calls or texts (message &amp; data
                                                rates may apply, msg frequency varies, reply <b>STOP</b> to opt out). Consent is not a condition of employment.
                                                {isCanada ? " (Canada – PIPEDA / CASL)." : " (USA – TCPA)."} See our{" "}
                                                <a
                                                    href="#/privacy"
                                                    className="underline text-[#e85812]"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    Privacy Policy
                                                </a>.
                                            </span>
                                        </label>
                                    )}
                                    {error && (
                                        <div
                                            className="text-[13px] text-red-600 font-body"
                                            data-testid="apply-error"
                                        >
                                            {error}
                                        </div>
                                    )}
                                    {renderResumeField()}
                                    {resumeError && (
                                        <div className="text-[12px] text-red-600" data-testid="apply-resume-error-form">
                                            {resumeError}
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="btn-primary w-full !py-3.5 !text-[15px]"
                                        data-testid="apply-submit-btn"
                                    >
                                        {submitting ? "Applying…" : "Apply & continue →"}
                                    </button>
                                    {saved && !user && (
                                        <button
                                            type="button"
                                            className="text-[12.5px] text-slate-500 hover:text-[#e85812] block mx-auto mt-1 underline"
                                            onClick={() => {
                                                clearSavedLead();
                                                setSaved(null);
                                                setName("");
                                                setPhone("");
                                                setEmail("");
                                            }}
                                            data-testid="apply-forget-btn"
                                        >
                                            Forget me on this device
                                        </button>
                                    )}
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
                        <div className="text-[11px] text-slate-500 max-w-[75%] leading-tight">
                            All trademarks belong to their owners. Dailyworkjobs is not affiliated with listed employers. 16+ only. Consent is not required to visit the employer&apos;s site.
                        </div>
                        <div className="relative flex items-center gap-2">
                            <button
                                className={`btn-ghost !py-2 !px-3 !text-xs ${isSaved ? "!text-[#e85812] !bg-[#fff2ea]" : ""}`}
                                onClick={toggleBookmark}
                                disabled={savingBookmark}
                                data-testid="apply-bookmark-btn"
                                title={isSaved ? "Remove from saved" : "Save for later"}
                            >
                                {isSaved ? <BookmarkCheck className="w-3.5 h-3.5 fill-[#e85812]" /> : <Bookmark className="w-3.5 h-3.5" />}
                                {isSaved ? "Saved" : "Save"}
                            </button>
                            <button
                                className="btn-ghost !py-2 !px-3 !text-xs"
                                onClick={() => setShowShare((s) => !s)}
                                data-testid="apply-share-btn"
                            >
                                <Share2 className="w-3.5 h-3.5" /> Share
                            </button>
                            {showShare && (
                                <ShareMenu
                                    url={shareUrl}
                                    text={shareText}
                                    onClose={() => setShowShare(false)}
                                    onToast={onToast}
                                />
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
